import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { IsraeliTaxService } from '@core/services/israelitax.service';
import { LanguageService } from '@core/services/language.service';
import { ItaStatus } from '@core/models/ita-status.model';

const DISMISS_KEY = 'banner_ita_reconnect_dismissed';

/**
 * Tells a business whose Tax Authority connection was granted for the old scope to
 * reconnect before the Tax Authority stops accepting it (1 November 2026). Until it
 * does, invoices above the threshold stop getting allocation numbers on that date.
 * Hidden for the rest of the session once dismissed; the Business Settings card
 * keeps saying it.
 */
@Component({
  selector: 'app-ita-reconnect-banner',
  standalone: true,
  imports: [CommonModule, RouterModule, MatButtonModule, MatIconModule, TranslateModule],
  templateUrl: './ita-reconnect-banner.component.html',
  styleUrls: ['./ita-reconnect-banner.component.scss'],
})
export class ItaReconnectBannerComponent implements OnInit {
  private readonly ita = inject(IsraeliTaxService);
  private readonly langSvc = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly status = signal<ItaStatus | null>(null);
  private readonly dismissed = signal(ItaReconnectBannerComponent.readDismissed());

  readonly show = computed(() => !this.dismissed() && !!this.status()?.connected && !!this.status()?.scopeOutdated);
  readonly reconnectBy = computed(() => this.status()?.reconnectBy ?? null);
  readonly isRtl = computed(() => this.langSvc.isRTL());

  ngOnInit(): void {
    this.ita.getStatus().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: status => this.status.set(status),
      error: () => { /* Not shown when the status cannot be read */ }
    });
  }

  dismiss(): void {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* storage blocked: hide for this view only */ }
    this.dismissed.set(true);
  }

  private static readDismissed(): boolean {
    try { return !!sessionStorage.getItem(DISMISS_KEY); } catch { return false; }
  }
}
